import React, { useEffect, useState } from 'react';
import {
	Cell,
	Tooltip,
	Legend,
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	ResponsiveContainer,
	LineChart,
	Line,
	LabelList,
	Area,
	AreaChart,
	PieChart,
	Pie,
} from 'recharts';
import moment from 'moment';
import { formatCurrency, formatCurrencyNew, request } from '../../../services/utilities';
import TableLoading from '../../../components/TableLoading';
import { count } from 'rxjs';
import SlidingPane from '../../../components/SlidingPane';
import { Card, Col, Row, Statistic } from 'antd';
import AnalysisBystaff from '../../../components/Modals/frontDesk/staffAnalyticsmodal';
import { TimeRangeDropdownAlt3 as TimeRangeDropdown } from '../../../components/TimeRangeDropdownAlt3';

// const 3 ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc0cb', '#faad14'];

const COLORS = [
	'#FF8042', // orange
	// '#FF4444', // red
	'#00C49F', // green
	'#FFBB28', // yellow
	'#0088FE', // blue
	'#8884d8', // purple
	'#FF6699', // pink
	'#A28EFF', // light purple
	'#33CCFF', // sky blue
	'#22BB33', // dark green
];

const mapTimeRangeToApiFilter = range => {
	switch (range) {
		case 'today':
			return 'today';
		case 'pastWeek':
			return 'past-week';
		case 'past30Days':
			return 'past-month';
		case 'lastMonth':
			return 'last-month';
		case 'pastQuarter':
			return 'past-quarter';
		case 'pastSixMonths':
			return 'past-six-months';
		case 'pastYear':
			return 'past-year';
		case 'pastThreeYears':
			return 'past-three-years';
		case 'lastYear':
			return 'last-year';
		case 'allTime':
			return 'all-time';
		default:
			return 'past-month';
	}
};

// const TimeRangeDropdown = ({ value, onChange, chartId }) => {
// 	return (
// 		<select
// 			value={value}
// 			onChange={e => onChange(e.target.value, chartId)}
// 			className="form-control form-control-sm rounded"
// 			style={{ width: '140px', marginBottom: '10px', borderRadius: '50px' }}
// 		>
// 			<option value="today">Today</option>
// 			<option value="pastWeek">Past Week</option>
// 			<option value="past30Days">Past 30 Days</option>
// 			<option value="pastYear">Past Year</option>
// 			<option value="allTime">All Time</option>
// 		</select>
// 	);
// };

// const TimeRangeDropdown = ({ value, onChange, chartId }) => {
// 	return (
// 		<select
// 			value={value}
// 			onChange={e => onChange(e.target.value, chartId)}
// 			className="form-control form-control-sm rounded"
// 			style={{ width: '160px', marginBottom: '10px', borderRadius: '50px' }}
// 		>
// 			<option value="today">Today</option>
// 			<option value="pastWeek">Past Week</option>
// 			<option value="past30Days">Past 30 Days</option>
// 			<option value="lastMonth">Last Month</option>
// 			<option value="pastQuarter">Past Quarter</option>
// 			<option value="pastSixMonths">Past Six Months</option>
// 			<option value="pastYear">Past Year</option>
// 			<option value="lastYear">Last Year</option>
// 			<option value="pastThreeYears">Past Three Years</option>
// 			<option value="allTime">All Time</option>
// 		</select>
// 	);
// };

// Mock API service functions
const PaypointApiService = {
	getTotalRevenue: async timeRange => {
		return new Promise(resolve => {
			setTimeout(() => {
				resolve({
					totalRevenue: Math.floor(Math.random() * 1000000) + 500000,
					avgRevenuePerPatient: Math.floor(Math.random() * 5000) + 2000,
					avgRevenuePerTransaction: Math.floor(Math.random() * 1000) + 500,
				});
			}, 500);
		});
	},

	getRevenueByDate: async timeRange => {
		return new Promise(resolve => {
			setTimeout(() => {
				const data = generateTimeRangeData(timeRange);
				resolve(data);
			}, 600);
		});
	},

	getRevenueSources: async timeRange => {
		return new Promise(resolve => {
			setTimeout(() => {
				const services = ['Consultation', 'Lab Tests', 'Surgery', 'Imaging', 'Pharmacy'];
				const topSources = services
					.map(service => ({
						name: service,
						revenue: Math.floor(Math.random() * 200000) + 50000,
					}))
					.sort((a, b) => b.revenue - a.revenue)
					.slice(0, 5);

				const bottomSources = services
					.map(service => ({
						name: service,
						revenue: Math.floor(Math.random() * 20000) + 1000,
					}))
					.sort((a, b) => a.revenue - b.revenue)
					.slice(0, 5);

				resolve({ topSources, bottomSources });
			}, 700);
		});
	},

	getRevenueByDepartment: async timeRange => {
		return new Promise(resolve => {
			setTimeout(() => {
				const departments = ['Cardiology', 'Pediatrics', 'Orthopedics', 'Radiology', 'General Medicine'];
				const data = departments.map(dept => ({
					name: dept,
					revenue: Math.floor(Math.random() * 300000) + 100000,
				}));
				resolve(data);
			}, 800);
		});
	},

	getStaffPerformance: async timeRange => {
		return new Promise(resolve => {
			setTimeout(() => {
				const staff = ['John Doe', 'Jane Smith', 'Alice Brown', 'Bob Johnson', 'Carol White'];
				const data = staff.map(s => ({
					name: s,
					revenue: Math.floor(Math.random() * 150000) + 50000,
					transactions: Math.floor(Math.random() * 200) + 50,
				}));
				resolve(data);
			}, 900);
		});
	},

	getTransactionData: async timeRange => {
		return new Promise(resolve => {
			setTimeout(() => {
				const transactionVolume = generateTimeRangeData(timeRange);
				const peakHours = Array.from({ length: 24 }, (_, i) => ({
					name: `${i}:00`,
					transactions: Math.floor(Math.random() * 50) + (i >= 9 && i <= 17 ? 20 : 5),
				}));

				resolve({ transactionVolume, peakHours });
			}, 1000);
		});
	},

	getPaymentMethods: async timeRange => {
		return new Promise(resolve => {
			setTimeout(() => {
				const methods = ['Cash', 'POS', 'Transfer', 'HMO'];
				const data = methods.map(method => ({
					name: method,
					value: Math.floor(Math.random() * 100000) + 50000,
				}));
				resolve(data);
			}, 1100);
		});
	},

	getTransactionStatus: async timeRange => {
		return new Promise(resolve => {
			setTimeout(() => {
				const data = generateTimeRangeData(timeRange).map(item => ({
					name: item.name,
					pending: Math.floor(Math.random() * 50) + 10,
					completed: Math.floor(Math.random() * 200) + 100,
					failed: Math.floor(Math.random() * 20) + 5,
				}));
				resolve({
					statusTrends: data,
					failedTransactions: Math.floor(Math.random() * 50) + 10,
					pendingVsCompleted: [
						{ name: 'Pending', value: Math.floor(Math.random() * 50) + 10 },
						{ name: 'Completed', value: Math.floor(Math.random() * 200) + 100 },
						{ name: 'Failed', value: Math.floor(Math.random() * 20) + 5 },
					],
				});
			}, 1200);
		});
	},

	getVoucherData: async timeRange => {
		return new Promise(resolve => {
			setTimeout(() => {
				const departments = ['Cardiology', 'Pediatrics', 'Orthopedics', 'Radiology', 'General Medicine'];
				const staff = ['John Doe', 'Jane Smith', 'Alice Brown', 'Bob Johnson', 'Carol White'];

				const voucherUsage = departments.map(dept => ({
					name: dept,
					value: Math.floor(Math.random() * 50000) + 10000,
				}));

				const voucherTrends = generateTimeRangeData(timeRange).map(item => ({
					name: item.name,
					vouchers: Math.floor(Math.random() * 50) + 10,
				}));

				const topUsers = staff
					.map(s => ({
						name: s,
						vouchers: Math.floor(Math.random() * 50) + 10,
					}))
					.sort((a, b) => b.vouchers - a.vouchers)
					.slice(0, 5);

				resolve({
					voucherUsage,
					voucherTrends,
					topVoucherUsers: topUsers,
					voucherRedemptionRate: Math.random() * 0.8 + 0.2,
				});
			}, 1300);
		});
	},
};

// Helper function to generate time-based data
const generateTimeRangeData = range => {
	let periods;
	switch (range) {
		case 'today':
			periods = 1;
			break;
		case 'pastWeek':
			periods = 7;
			break;
		case 'past30Days':
			periods = 30;
			break;
		case 'pastYear':
			periods = 12;
			break;
		case 'allTime':
			periods = 5;
			break;
		default:
			periods = 30;
	}

	return Array.from({ length: periods }, (_, i) => {
		const dateKey =
			range === 'pastYear'
				? moment()
						.subtract(11 - i, 'months')
						.format('MMM')
				: range === 'allTime'
				? moment()
						.subtract(4 - i, 'years')
						.format('YYYY')
				: moment()
						.subtract(periods - 1 - i, 'days')
						.format('MMM D');
		return {
			name: dateKey,
			revenue: Math.floor(Math.random() * (range === 'allTime' ? 1000000 : range === 'pastYear' ? 200000 : 5000)) + 10000,
			transactions: Math.floor(Math.random() * (range === 'allTime' ? 5000 : range === 'pastYear' ? 800 : 50)) + 30,
		};
	});
};

const PaypointStaffAnalytics = ({ selectedStaff }) => {
	const [timeRanges, setTimeRanges] = useState({
		totalRevenue: 'past30Days',
		revenueByDate: 'past30Days',
		revenueSources: 'past30Days',
		revenueByDepartment: 'past30Days',
		avgRevenue: 'past30Days',
		staffPerformance: 'past30Days',
		transactions: 'past30Days',
		paymentMethods: 'past30Days',
		voucherUsage: 'past30Days',
		voucherTrends: 'past30Days',
		transactionStatusTrends: 'past30Days',
		transactionTypeCount: 'past30Days',
	});

	const [loadingStates, setLoadingStates] = useState({
		totalRevenue: false,
		revenueByDate: false,
		revenueSources: false,
		revenueByDepartment: false,
		avgRevenue: false,
		staffPerformance: false,
		transactions: false,
		paymentMethods: false,
		voucherUsage: false,
		voucherTrends: false,
		transactionStatusTrends: false,
		transactionTypeCount: false,
	});

	const [totalRevenue, setTotalRevenue] = useState(0);
	const [revenueByDate, setRevenueByDate] = useState([]);
	const [topRevenueSources, setTopRevenueSources] = useState([]);
	const [bottomRevenueSources, setBottomRevenueSources] = useState([]);
	const [revenueByDepartment, setRevenueByDepartment] = useState([]);
	const [avgRevenuePerPatient, setAvgRevenuePerPatient] = useState(0);
	const [avgRevenuePerTransaction, setAvgRevenuePerTransaction] = useState(0);
	const [staffRevenue, setStaffRevenue] = useState([]);
	const [staffTransactions, setStaffTransactions] = useState([]);
	const [transactionVolume, setTransactionVolume] = useState([]);
	const [peakPaymentHours, setPeakPaymentHours] = useState([]);
	const [paymentMethods, setPaymentMethods] = useState([]);
	const [allPaymentMethods, setAllPaymentMethods] = useState([]);
	const [topPaymentMethods, setTopPaymentMethods] = useState([]);
	const [bottomPaymentMethods, setBottomPaymentMethods] = useState([]);
	const [voucherUsage, setVoucherUsage] = useState([]);
	const [voucherTrends, setVoucherTrends] = useState([]);
	const [topVoucherUsers, setTopVoucherUsers] = useState([]);
	const [voucherRedemptionRate, setVoucherRedemptionRate] = useState(0);
	const [transactionStatusTrends, setTransactionStatusTrends] = useState([]);
	const [voucherRatio, setVoucherRatio] = useState();
	const [transactionTypeCount, setTransactionTypeCount] = useState([]);
	const [modal, setModal] = useState(false);
	const [modalAlt, setModalAlt] = useState(false);

	const [allRevenue, SetAllRevenue] = useState([]);
	const [transSummary, setTransSummary] = useState();

	const fetchTotalRevenue = async range => {
		setLoadingStates(prev => ({ ...prev, totalRevenue: true }));
		const dateFilter = mapTimeRangeToApiFilter(range);
		try {
			const data = await PaypointApiService.getTotalRevenue(range);
			const res = await request(`analytics/transaction-total?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`, 'GET', true);
			const voucherRes = await request(
				`analytics/transaction-voucher-ratio?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`,
				'GET',
				true
			);
			setVoucherRatio(voucherRes?.ratio);
			setTotalRevenue(...res.result);
			setAvgRevenuePerPatient(data.avgRevenuePerPatient);
			setAvgRevenuePerTransaction(data.avgRevenuePerTransaction);
		} catch (error) {
			console.error('Error fetching total revenue:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, totalRevenue: false }));
		}
	};

	const fetchRevenueByDate = async range => {
		const dateFilter = mapTimeRangeToApiFilter(range);
		setLoadingStates(prev => ({ ...prev, revenueByDate: true }));
		try {
			const res = await request(`analytics/transaction-revenue-trend?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`, 'GET', true);

			const apiRespone = res?.result
				// ?.sort(() => Math.random() - 0.5)
				?.map(x => ({
					name:
						x?.date ||
						x?.day ||
						x?.week ||
						(dateFilter == 'past-three-years' && moment(x?.month).format('MMM,YY')) ||
						(x?.month && moment(x?.month).format('MMMM')) ||
						x?.year ||
						x?.hour,
					count: parseInt(x?.count, 10),
					total_paid: parseInt(x?.totalAmountPaid, 10),
					total_amount: parseInt(x?.totalAmount, 10),
				}));
			// console.log(res?.result, 'result...!')
			setRevenueByDate(apiRespone);
		} catch (error) {
			console.error('Error fetching revenue by date:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, revenueByDate: false }));
		}
	};

	const fetchRevenueSources = async range => {
		const dateFilter = mapTimeRangeToApiFilter(range);
		setLoadingStates(prev => ({ ...prev, revenueSources: true }));
		try {
			const { topSources, bottomSources } = await PaypointApiService.getRevenueSources(range);
			const res = await request(`analytics/transaction-service-revenue?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`, 'GET', true);
			const bottomSourcesApi = res?.bottom10?.map(x => ({
				name: x?.service || x?.source,
				// source:x?.source,
				revenue: parseInt(x?.totalRevenue, 10),
				count: parseInt(x?.totaltransactions, 10),
			}));
			const topSourcesApi = res?.top10?.map(x => ({
				name: x?.service || x?.source,
				// source:x?.source,
				revenue: parseInt(x?.totalRevenue, 10),
				count: parseInt(x?.totaltransactions, 10),
			}));

			const allRevenueSource = res?.result?.map(x => ({
				name: x?.service || x?.source,
				// source:x?.source,
				revenue: parseInt(x?.totalRevenue, 10),
				count: parseInt(x?.totaltransactions, 10),
				average: parseInt(x?.averageAmount, 10),
				totalRevenue: parseInt(x?.totalRevenue, 10),
				transactionsFormatted: x?.totalTransactionsFormatted,
				averageAmountFormatted: x?.averageAmountFormatted,
			}));

			const summary = res?.summary;

			setTransSummary(summary);

			setTopRevenueSources(topSourcesApi);
			setBottomRevenueSources(bottomSourcesApi);
			SetAllRevenue(allRevenueSource);
		} catch (error) {
			console.error('Error fetching revenue sources:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, revenueSources: false }));
		}
	};

	const fetchRevenueByDepartment = async range => {
		const dateFilter = mapTimeRangeToApiFilter(range);
		setLoadingStates(prev => ({ ...prev, revenueByDepartment: true }));
		try {
			const data = await PaypointApiService.getRevenueByDepartment(range);
			const res = await request(`analytics/transaction-sources-revenue?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`, 'GET', true);
			const apiRespone = res?.result?.map(x => ({
				name: x?.source,
				revenue: parseInt(x?.totaltransactions, 10),
			}));
			setRevenueByDepartment(apiRespone);
		} catch (error) {
			console.error('Error fetching revenue by department:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, revenueByDepartment: false }));
		}
	};

	const fetchStaffPerformance = async range => {
		setLoadingStates(prev => ({ ...prev, staffPerformance: true }));
		try {
			const data = await PaypointApiService.getStaffPerformance(range);
			setStaffRevenue(data);
			setStaffTransactions(data);
		} catch (error) {
			console.error('Error fetching staff performance:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, staffPerformance: false }));
		}
	};

	const fetchTransactionData = async range => {
		setLoadingStates(prev => ({ ...prev, transactions: true }));
		const dateFilter = mapTimeRangeToApiFilter(range);
		try {
			const { transactionVolume, peakHours } = await PaypointApiService.getTransactionData(range);
			setPeakPaymentHours(peakHours);
			const res = await request(`analytics/transaction-revenue-trend?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`, 'GET', true);
			const data = await PaypointApiService.getRevenueByDate(range);
			const apiRespone = res?.result.map(x => ({
				name:
					x?.date ||
					(dateFilter == 'past-three-years' && x?.month && moment(x?.month).format('MMMM, YY')) ||
					(x?.month && moment(x?.month).format('MMMM')) ||
					x?.day ||
					x?.week ||
					x?.year ||
					x?.hour,
				transactions: parseInt(x?.count, 10) || 0,
			}));
			setTransactionVolume(apiRespone);
		} catch (error) {
			console.error('Error fetching transaction data:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, transactions: false }));
		}
	};

	const fetchPaymentMethods = async range => {
		setLoadingStates(prev => ({ ...prev, paymentMethods: true }));
		const dateFilter = mapTimeRangeToApiFilter(range);
		try {
			const data = await PaypointApiService.getPaymentMethods(range);
			const res = await request(`analytics/transaction-payment-methods?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`, 'GET', true);
			const apiResponeTop = res?.result?.slice(0, 10)?.map(x => ({
				name: x?.type,
				value: parseInt(x?.totaltransactions, 10 || 0),
			}));

			const apiResponeBottom = res?.result
				?.slice(-10)
				?.reverse()
				?.map(x => ({
					name: x?.type,
					value: parseInt(x?.totaltransactions, 10 || 0),
				}));

			const apiResponeAll = res?.result?.map(x => ({
				name: x?.type,
				value: parseInt(x?.totaltransactions, 10 || 0),
			}));

			setAllPaymentMethods(apiResponeAll);
			setPaymentMethods(data);
			setTopPaymentMethods(apiResponeTop);
			setBottomPaymentMethods(apiResponeBottom);
		} catch (error) {
			console.error('Error fetching payment methods:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, paymentMethods: false }));
		}
	};

	const fetchTransactionStatus = async range => {
		setLoadingStates(prev => ({ ...prev, transactionStatusTrends: true }));
		const dateFilter = mapTimeRangeToApiFilter(range);
		try {
			const { statusTrends, failedTransactions, pendingVsCompleted } = await PaypointApiService.getTransactionStatus(range);
			const res = await request(`analytics/transaction-status-trend?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`, 'GET', true);

			const apiRespone = res?.result.map(x => ({
				completed: parseInt(x?.completedCount, 10),
				pending: parseInt(x?.pendingCount, 10),
				name:
					x?.date ||
					x?.day ||
					x?.week ||
					(dateFilter == 'past-three-years' && moment(x?.month).format('MMM,YY')) ||
					(x?.month && moment(x?.month).format('MMM')) ||
					x?.year,
				cancelled: parseInt(x?.deletedCount, 10),
				total: parseInt(x?.count, 10),
			}));

			setTransactionStatusTrends(apiRespone);
		} catch (error) {
			console.error('Error fetching transaction status:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, transactionStatusTrends: false }));
		}
	};

	const fetchTransactionTypeCount = async range => {
		const dateFilter = mapTimeRangeToApiFilter(range);
		setLoadingStates(prev => ({ ...prev, transactionTypeCount: true }));
		try {
			const res = await request(`analytics/transaction-types-count?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`, 'GET', true);

			const creditTotal = parseInt(res?.result?.find(x => x?.type === 'credit')?.totaltransactions || 0, 10);

			const debitTotal = parseInt(res?.result?.find(x => x?.type === 'debit')?.totaltransactions || 0, 10);

			const apiRespone = [
				{ name: 'paid', revenue: creditTotal },
				{ name: 'Pending', revenue: creditTotal - debitTotal },
			];

			setTransactionTypeCount(apiRespone);
		} catch (error) {
			console.error('Error fetching transaction type count:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, transactionTypeCount: false }));
		}
	};

	const fetchVoucherData = async range => {
		setLoadingStates(prev => ({ ...prev, voucherTrends: true }));
		const dateFilter = mapTimeRangeToApiFilter(range);
		try {
			const { voucherUsage, voucherTrends, topVoucherUsers, voucherRedemptionRate } = await PaypointApiService.getVoucherData(range);
			const res = await request(`analytics/transaction-voucher-trend?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`, 'GET', true);
			// const response = await request(`analytics/transaction-voucher-services?dateFilter=${dateFilter}`, 'GET', true);

			const apiResponetrends = res?.result.map(x => ({
				name:
					x?.year ||
					x?.month ||
					x?.week ||
					x?.day ||
					x?.date ||
					(dateFilter == 'past-three-years' && x?.month && moment(x?.month).format('MMMM, YY')) ||
					(x?.month && moment(x?.month).format('MMMM')),
				value: parseInt(x?.count, 10),
			}));

			// const apiResponseSource = response?.result.map(x => ({
			// 	name: x?.service,
			// 	value: parseInt(x?.totaltransactions, 10),
			// }));

			// setVoucherUsage(apiResponseSource);
			setVoucherTrends(apiResponetrends);

			setTopVoucherUsers(topVoucherUsers);
			setVoucherRedemptionRate(voucherRedemptionRate);
		} catch (error) {
			console.error('Error fetching voucher data:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, voucherTrends: false }));
		}
	};

	const fetchVoucherData2 = async range => {
		setLoadingStates(prev => ({ ...prev, voucherUsage: true }));
		const dateFilter = mapTimeRangeToApiFilter(range);
		try {
			const { voucherUsage, voucherTrends, topVoucherUsers, voucherRedemptionRate } = await PaypointApiService.getVoucherData(range);
			const response = await request(
				`analytics/transaction-voucher-services?dateFilter=${dateFilter}&staffId=${selectedStaff?.id}`,
				'GET',
				true
			);

			const apiResponseSource = response?.result.map(x => ({
				name: x?.source,
				value: parseInt(x?.totaltransactions, 10),
			}));

			setVoucherUsage(apiResponseSource);

			// setTopVoucherUsers(topVoucherUsers);
			// setVoucherRedemptionRate(voucherRedemptionRate);
		} catch (error) {
			console.error('Error fetching voucher data:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, voucherUsage: false }));
		}
	};

	const handleTimeRangeChange = (range, chartId) => {
		setTimeRanges(prev => ({ ...prev, [chartId]: range }));

		// Call the appropriate API based on which chart's time range changed
		switch (chartId) {
			case 'totalRevenue':
				fetchTotalRevenue(range);
				break;
			case 'revenueByDate':
				fetchRevenueByDate(range);
				break;
			case 'revenueSources':
				fetchRevenueSources(range);
				break;
			case 'revenueByDepartment':
				fetchRevenueByDepartment(range);
				break;
			case 'staffPerformance':
				fetchStaffPerformance(range);
				break;
			case 'transactions':
				fetchTransactionData(range);
				break;
			case 'paymentMethods':
				fetchPaymentMethods(range);
				break;
			case 'transactionStatusTrends':
				fetchTransactionStatus(range);
				break;
			case 'voucherUsage':
				fetchVoucherData2(range);
				break;
			case 'voucherTrends':
				fetchVoucherData(range);
				break;
			case 'transactionTypeCount':
				fetchTransactionTypeCount(range);
				break;
			default:
				break;
		}
	};

	useEffect(() => {
		// Initial data fetch
		fetchTotalRevenue(timeRanges.totalRevenue);
		fetchRevenueByDate(timeRanges.revenueByDate);
		fetchRevenueSources(timeRanges.revenueSources);
		fetchRevenueByDepartment(timeRanges.revenueByDepartment);
		fetchStaffPerformance(timeRanges.staffPerformance);
		fetchTransactionData(timeRanges.transactions);
		fetchPaymentMethods(timeRanges.paymentMethods);
		fetchTransactionStatus(timeRanges.transactionStatusTrends);
		fetchVoucherData2(timeRanges.voucherUsage);
		fetchVoucherData(timeRanges.voucherTrends);
		fetchTransactionTypeCount(timeRanges.transactionTypeCount);
	}, []);

	const CustomTooltip = ({ active, payload, label }) => {
		if (active && payload && payload.length) {
			return (
				<div
					className="custom-tooltip"
					style={{ backgroundColor: '#fff', textTransform: 'capitalize', padding: '10px', border: '1px solid #ccc' }}
				>
					<p className="label">{`${label}`}</p>
					{payload.map((entry, index) => (
						<p key={`item-${index}`} style={{ color: entry.color || '#000' }}>
							{`${entry.name}: ${entry.value}`}
						</p>
					))}
				</div>
			);
		}
		return null;
	};

	const renderCustomLabelAlt = ({ active, payload, label }) => {
		if (active && payload && payload.length) {
			const total = payload[0]?.payload?.total;
			const count = payload[0]?.payload?.count; // Add this line

			return (
				<div
					style={{
						background: '#fff',
						padding: '8px 12px',
						border: '1px solid #ccc',
						borderRadius: '6px',
						fontSize: '12px',
					}}
				>
					<p style={{ fontWeight: 'bold', marginBottom: '4px' }}>{label}</p>
					{payload.map((entry, index) => (
						<p key={`item-${index}`} style={{ color: entry.color, margin: 0 }}>
							{entry.name}: {Number(entry.value).toLocaleString()}
						</p>
					))}
					{/* Add the count/total display */}
					{total !== undefined && (
						<p style={{ marginTop: '6px', fontWeight: 'bold', color: '#333' }}>Total: {Number(total).toLocaleString()}</p>
					)}
					{/* Or if you want to use 'count' instead of 'total': */}
					{count !== undefined && (
						<p style={{ marginTop: '6px', fontWeight: 'bold', color: '#333' }}>Count: {Number(count).toLocaleString()}</p>
					)}
				</div>
			);
		}
		return null;
	};

	const formatBarLabel = ({ x, y, value, width }) => (
		<text x={x + width / 2} y={y - 6} textAnchor="middle" fill="#333" fontSize={12}>
			{value?.toLocaleString()}
		</text>
	);

	// const CustomTooltipAlt = ({ active, payload, label }) => {
	// 	if (!active || !payload?.length) return null;

	// 	const data = payload[0]?.payload;

	// 	return (
	// 		<div className="custom-tooltip" style={{ backgroundColor: '#fff', border: '1px solid #ccc', padding: 10 }}>
	// 			<p>
	// 				<strong>{String(label).charAt(0).toUpperCase() + String(label).slice(1)}</strong>
	// 			</p>
	// 			<p>Total Paid: ₦{Number(data?.total_paid).toLocaleString()}</p>
	// 			<p>Total Amount: ₦{Number(data?.total_amount).toLocaleString()}</p>
	// 		</div>
	// 	);
	// };

	// console.log(revenueByDate, 'Revenue Over Time');

	const CustomTooltipAlt = ({ active, payload, label }) => {
		if (active && payload && payload.length) {
			const { name, count, total_amount, total_paid } = payload[0].payload;
			console.log(payload[0].payload, 'payload[0].payload');
			return (
				<div style={{ background: '#fff', border: '1px solid #ccc', padding: '8px', fontSize: '12px' }}>
					<p>
						<strong>Date:</strong> {name}
					</p>
					<p>
						<strong>Count:</strong> {Number(count).toLocaleString()}
					</p>
					<p>
						<strong>Total Amount:</strong> ₦{Number(total_amount).toLocaleString()}
					</p>
					<p>
						<strong>Total Paid:</strong> ₦{Number(total_paid).toLocaleString()}
					</p>
				</div>
			);
		}
		return null;
	};

	return (
		<div className="content-i">
			<div className="content-box">
				{/* Revenue Overview */}
				<div className="row col-12">
					<div className="col-sm-6">
						<div className="element-wrapper">
							<h6 className="element-header">Total Revenue</h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdown value={timeRanges.totalRevenue} onChange={handleTimeRangeChange} chartId="totalRevenue" />
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								Total amount collected across all transactions.
							</p>
							<div className="element-box">
								{loadingStates.totalRevenue ? (
									<TableLoading />
								) : (
									<div className="el-tablo">
										<div className="value" style={{ fontSize: '25px', color: '#52c41a' }}>
											{formatCurrencyNew(totalRevenue?.total_amount) || 0}
										</div>
										<div className="label">Total Revenue Collected</div>
									</div>
								)}
							</div>
						</div>
					</div>
					<div className="col-sm-6">
						<div className="element-wrapper">
							<h6 className="element-header">Voucher Redemption Rate</h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdown value={timeRanges.voucherUsage} onChange={handleTimeRangeChange} chartId="voucherUsage" />
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								Percentage of issued vouchers that were redeemed.
							</p>
							<div className="element-box">
								{loadingStates.voucherUsage ? (
									<TableLoading />
								) : (
									<div className="el-tablo">
										<div className="value" style={{ fontSize: '25px', color: '#52c41a' }}>
											{voucherRatio?.toFixed(1) || 0}%
										</div>
										<div className="label">Voucher Redemption Rate</div>
									</div>
								)}
							</div>
						</div>
					</div>

					<div className="col-12">
						<div className="element-wrapper">
							<h6 className="element-header">Revenue Over Time</h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdown value={timeRanges.revenueByDate} onChange={handleTimeRangeChange} chartId="revenueByDate" />
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								This chart displays the revenue trends observed over the selected time period, highlighting fluctuations in income and
								providing insights into financial performance patterns across the specified duration.
							</p>
							<div className="element-box">
								{loadingStates.revenueByDate ? (
									<TableLoading />
								) : revenueByDate.length > 0 ? (
									<div style={{ width: '100%', height: 400 }}>
										{/* <ResponsiveContainer>
											<LineChart data={revenueByDate} margin={{ top: 50, right: 30, left: 60, bottom: 20 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<YAxis
													domain={[0, 'auto']}
													tickFormatter={value => formatCurrency(value)}
													// tickFormatter={(value) => Number(value).toLocaleString()}
												/>
												<XAxis
													dataKey="name"
													// tick={{ fontSize: 12 }}
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={0}
													tickFormatter={value => {
														const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<Tooltip content={CustomTooltipAlt} />
												<Legend />
												<Line
													type="monotone"
													dataKey="total_amount"
													position="top"
													stroke="#FF8042"
													strokeWidth={2}
													name="Total Amount"
													connectNulls
												></Line>

												<Line
													type="monotone"
													dataKey="total_paid"
													position="top"
													stroke="#0088FE"
													strokeWidth={2}
													name="Total Paid"
													connectNulls
												></Line>
											</LineChart>
										</ResponsiveContainer> */}
										<ResponsiveContainer>
											<AreaChart data={revenueByDate} margin={{ top: 50, right: 30, left: 60, bottom: 60 }}>
												<defs>
													<linearGradient id="colorTotalAmount" x1="0" y1="0" x2="0" y2="1">
														<stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} />
														<stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
													</linearGradient>
													<linearGradient id="colorTotalPaid" x1="0" y1="0" x2="0" y2="1">
														<stop offset="5%" stopColor="green" stopOpacity={0.8} />
														<stop offset="95%" stopColor="green" stopOpacity={0} />
													</linearGradient>
												</defs>

												<CartesianGrid strokeDasharray="3 3" />
												<YAxis style={{ fontSize: '12px' }} tickFormatter={value => formatCurrencyNew(value)} domain={[0, 'auto']} />
												<XAxis
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={timeRanges?.revenueByDate == 'pastQuarter' ? 2 : 0}
													dataKey="name"
													tick={{ fontSize: 12 }}
												/>
												<Tooltip content={CustomTooltipAlt} />
												<Legend wrapperStyle={{ marginBottom: '-60px' }} />

												<Area
													type="monotone"
													dataKey="total_amount"
													stroke="#8884d8"
													fill="url(#colorTotalAmount)"
													name="Total Amount"
													connectNulls
													position="top"
													strokeWidth={2}
												></Area>

												<Area
													type="monotone"
													dataKey="total_paid"
													stroke="green"
													fill="url(#colorTotalPaid)"
													name="Total Paid"
													connectNulls
													position="top"
													strokeWidth={2}
												></Area>
											</AreaChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className=" w-100 card d-flex justify-content-center align-items-center" style={{ height: '50px' }}>
										<p className=" mt-6" style={{ paddingTop: '50px' }}>
											No Record for the selected time
										</p>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>

				{/* Revenue Sources */}
				<div className="row col-12">
					<div className="col-sm-6">
						<div className="element-wrapper">
							<h6 className="element-header">Top 5 Revenue Sources</h6>
							<div className="d-flex justify-content-end mb-5 w-100">
								{/* <TimeRangeDropdown value={timeRanges.revenueSources} onChange={handleTimeRangeChange} chartId="revenueSources" /> */}
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								Highlights the top five revenue-generating services, providing a clear view of the offerings that contribute most to the
								organization’s financial performance..
							</p>
							<div className="element-box">
								{loadingStates.revenueSources ? (
									<TableLoading />
								) : topRevenueSources.length > 0 ? (
									<div style={{ width: '100%', height: 400 }}>
										{/* <div className=' w-100 d-flex justify-content-end'>
											<button className=' btn btn-primary'>View All</button>
										</div> */}
										<ResponsiveContainer width="100%" height={400}>
											<BarChart
												data={topRevenueSources.slice(0, 5)}
												margin={{ top: 20, right: 20, left: 20, bottom: 60 }} // More left margin for category labels
											>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													style={{ fontSize: '12px' }}
													dataKey="name"
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={0}
													tickFormatter={value => {
														const truncated = value.length > 15 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis
													// angle={-60}
													textAnchor="end"
													style={{ fontSize: '12px' }}
													tickFormatter={value => formatCurrencyNew(value)}
													domain={[0, 'auto']}
												/>
												<Tooltip
													formatter={value => formatCurrencyNew(Number(value))}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>
												{/* <Legend /> */}
												<Bar dataKey="revenue" style={{ textTransform: 'capitalize' }}>
													{bottomRevenueSources.map((entry, index) => (
														<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
													))}
													{/* <LabelList
														dataKey="revenue"
														position="top"
														style={{ fontSize: '12px', fill: '#333' }}
														formatter={value => `${Number(value)?.toLocaleString()}`}
													/> */}
												</Bar>
											</BarChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className=" w-100 card d-flex justify-content-center align-items-center" style={{ height: '50px' }}>
										<p className=" mt-6" style={{ paddingTop: '50px' }}>
											No Record for the selected time
										</p>
									</div>
								)}
							</div>
						</div>
					</div>
					<div className="col-sm-6">
						<div className="element-wrapper">
							<h6 className="element-header">Bottom 5 Revenue Sources</h6>
							<div className="d-flex justify-content-end w-100">
								<div>
									<div
										style={{ width: '100px', cursor: 'pointer', textAlign: 'center' }}
										onClick={() => setModal(true)}
										className="form-control form-control-sm text-white bg-primary rounded"
									>
										View All
									</div>
								</div>
								&nbsp;
								<TimeRangeDropdown value={timeRanges.revenueSources} onChange={handleTimeRangeChange} chartId="revenueSources" />
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								Showcases the bottom five revenue-generating services, offering insight into the least profitable offerings that may need
								evaluation or strategic improvement
							</p>
							<div className="element-box">
								{loadingStates.revenueSources ? (
									<TableLoading />
								) : bottomRevenueSources.length > 0 ? (
									<div style={{ width: '100%', height: 400 }}>
										{/* <div className=" w-100 d-flex justify-content-end">
											<button onClick={() => setModal(true)} className=" btn btn-primary">
												View All
											</button>
										</div> */}

										{/* modal */}

										{/* endmodal */}

										<ResponsiveContainer width="100%" height={400}>
											<BarChart
												// layout="vertical"
												data={bottomRevenueSources.slice(0, 5)}
												margin={{ top: 20, right: 30, left: 20, bottom: 60 }} // More left margin for category labels
											>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey="name"
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={0}
													tickFormatter={value => {
														const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis
													// angle={-60}
													textAnchor="end"
													style={{ fontSize: '12px' }}
													tickFormatter={value => formatCurrencyNew(value)}
													domain={[0, 'auto']}
												/>
												<Tooltip
													wrapperStyle={{ textTransform: 'capitalize' }}
													formatter={value => formatCurrencyNew(Number(value))}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>
												{/* <Legend /> */}
												<Bar dataKey="revenue" style={{ textTransform: 'capitalize' }}>
													{bottomRevenueSources.map((entry, index) => (
														<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
													))}
													{/* <LabelList
														dataKey="revenue"
														position="top"
														style={{ fontSize: '12px', fill: '#333' }}
														formatter={value => `${Number(value)?.toLocaleString()}`}
													/> */}
												</Bar>
											</BarChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className=" w-100 card d-flex justify-content-center align-items-center" style={{ height: '50px' }}>
										<p className=" mt-6" style={{ paddingTop: '50px' }}>
											No Record for the selected time
										</p>
									</div>
								)}
							</div>
						</div>

						<SlidingPane isOpen={modal}>
							<div className=" content-box">
								<div className=" d-flex justify-content-end w-100">
									<button className="btn btn-danger float-end" onClick={() => setModal(false)}>
										&times;
									</button>
								</div>

								<div className=" d-flex justify-content-end mt-5 t-5 align-items-center">
									<TimeRangeDropdown value={timeRanges.revenueSources} onChange={handleTimeRangeChange} chartId="revenueSources" />
								</div>
								<div className="row p-5 w-100 ">
									<div className="col-sm-12">
										<div className="element-wrapper">
											<h6 className="element-header">Key Performance Indicators</h6>

											<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
												High-level metrics showing overall metrics of reading over time.
											</p>
											<div className="element-box">
												{/* <TableLoading /> */}

												<Row gutter={16}>
													<Col span={8}>
														<Card>
															<Statistic title="Total Average Revenue" value={transSummary?.averageTransactionValueFormatted} suffix="" />
															<small className="text-muted">Average Transactions</small>
														</Card>
													</Col>
													<Col span={8}>
														<Card>
															<Statistic title="Total Revenue" value={transSummary?.totalRevenueFormatted} suffix="" />

															<small className="text-muted">Cumulative Revenue From --- to --</small>
														</Card>
													</Col>
													<Col span={8}>
														<Card>
															<Statistic title="Total Transactions" value={transSummary?.totalTransactions} suffix="" />
															{/* <Statistic
																					title="Survey Coverage Rate"
																					value={coverageRate > 0 ? (coverageRate).toFixed(1) : 0}
																					suffix=""
																				/> */}
															<small className="text-muted">Cumulative Transactions From --- to --</small>
														</Card>
													</Col>
													{/* <Col span={6}>
																		<Card>
																			<Statistic title="Lowest Scoring Area" value={0} suffix="/3" />
																			<small className="text-muted">Needs attention</small>
																		</Card>
																	</Col> */}
												</Row>
											</div>
										</div>
									</div>
								</div>

								<div className=" w-100 d-flex justify-content-center align-items-center flex-column">
									<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
										This chart showcases all revenue-generating services, providing a clear view of their performance over time and
										highlighting opportunities for growth or improvement.
									</p>
								</div>

								<div className=" p-5">
									<div className=" card w-100 p-5">
										<ResponsiveContainer width="100%" height={400}>
											<BarChart
												// layout="vertical"
												data={allRevenue}
												margin={{ top: 20, right: 30, left: 20, bottom: 60 }} // More left margin for category labels
											>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey="name"
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={0}
													tickFormatter={value => {
														const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis
													// angle={-60}
													textAnchor="end"
													style={{ fontSize: '12px' }}
													tickFormatter={value => formatCurrencyNew(value)}
													domain={[0, 'auto']}
												/>
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>
												{/* <Legend /> */}
												<Bar dataKey="revenue">
													{bottomRevenueSources.map((entry, index) => (
														<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
													))}
													{/* <LabelList
																			dataKey="revenue"
																			position="top"
																			style={{ fontSize: '12px', fill: '#333' }}
																			formatter={value => `${Number(value)?.toLocaleString()}`}
																		/> */}
												</Bar>
											</BarChart>
										</ResponsiveContainer>
									</div>
								</div>
							</div>
						</SlidingPane>
					</div>
				</div>

				{/* Revenue by Department */}
				<div className="row col-12">
					<div className="col-sm-12">
						<div className="element-wrapper">
							<h6 className="element-header">Transactions By Bill Source</h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdown value={timeRanges.revenueByDepartment} onChange={handleTimeRangeChange} chartId="revenueByDepartment" />
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								This chart provides a comprehensive breakdown of transaction count by service category.
							</p>
							<div className="element-box">
								{loadingStates.revenueByDepartment ? (
									<TableLoading />
								) : revenueByDepartment.length > 0 ? (
									<div style={{ width: '100%', height: 400 }}>
										<ResponsiveContainer width="100%" height={400}>
											<BarChart
												style={{ textTransform: 'capitalize' }}
												data={revenueByDepartment}
												margin={{ top: 20, right: 30, left: 20, bottom: 50 }}
											>
												<CartesianGrid strokeDasharray="3 3" />
												{/* <XAxis dataKey="name" /> */}
												<YAxis
													domain={[0, 'auto']}
													//  angle={-45}
													textAnchor="end"
													interval={0}
													tickFormatter={value => (value.length > 10 ? `${value.slice(0, 10)}...` : value)}
												/>
												<Tooltip content={<CustomTooltip />} />
												{/* <Legend /> */}
												<XAxis
													style={{ textTransform: 'capitalize' }}
													wrapperStyle={{ textTransform: 'capitalize' }}
													dataKey="name"
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={0}
													tickFormatter={value => {
														const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													style={{ textTransform: 'capitalize' }}
													wrapperStyle={{ textTransform: 'capitalize' }}
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>
												{/* <Legend /> */}
												<Bar
													dataKey="revenue"
													name="Count"
													style={{ textTransform: 'capitalize' }}
													wrapperStyle={{ textTransform: 'capitalize' }}
												>
													{revenueByDepartment.map((entry, index) => (
														<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
													))}
													<LabelList
														wrapperStyle={{ textTransform: 'capitalize' }}
														dataKey="revenue"
														position="top"
														style={{ fontSize: '12px', fill: '#333', textTransform: 'capitalize' }}
														formatter={value => `${Number(value)?.toLocaleString()}`}
													/>
												</Bar>
											</BarChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className=" w-100 card d-flex justify-content-center align-items-center" style={{ height: '50px' }}>
										<p className=" mt-6" style={{ paddingTop: '50px' }}>
											No Record for the selected time
										</p>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>

				{/* Transaction Overview */}
				<div className="row col-12">
					<div className="col-12">
						<div className="element-wrapper">
							<h6 className="element-header">Transaction Volume Over Time</h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdown value={timeRanges.transactions} onChange={handleTimeRangeChange} chartId="transactions" />
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								Illustrates transaction volumes over time, revealing activity trends, operational load, and fluctuations in service demand
								within the selected period.
							</p>
							<div className="element-box">
								{loadingStates.transactions ? (
									<TableLoading />
								) : transactionVolume.length > 0 ? (
									<div style={{ width: '100%', height: 400 }}>
										<ResponsiveContainer>
											<LineChart
												data={transactionVolume}
												margin={{ top: 60, right: 30, left: 20, bottom: 45 }} // increased top margin for labels
											>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey="name"
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={timeRanges.transactions === 'pastQuarter' ? 2 : 0}
													tickFormatter={value => {
														const truncated = value.length > 15 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>
												<Legend wrapperStyle={{ marginBottom: '-45px' }} />
												<Line type="monotone" dataKey="transactions" stroke="#8884d8" strokeWidth={2} name="Transactions">
													<LabelList
														dataKey="transactions"
														position="top"
														content={renderCustomLabelAlt}
														offset={10}
														style={{ fontSize: '12px', fill: '#333' }}
													/>
												</Line>
											</LineChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className=" w-100 card d-flex justify-content-center align-items-center" style={{ height: '50px' }}>
										<p className=" mt-6" style={{ paddingTop: '50px' }}>
											No Record for the selected time
										</p>
									</div>
								)}
							</div>
						</div>
					</div>

					{/* New Transaction Status Trends Chart */}
					<div className="col-12">
						<div className="element-wrapper">
							<h6 className="element-header">Transactions Status Trends</h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdown
									value={timeRanges.transactionStatusTrends}
									onChange={handleTimeRangeChange}
									chartId="transactionStatusTrends"
								/>
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								This chart displays the trends of pending, completed, and failed transactions over time, providing a clear view of system
								performance, user activity, and any potential issues affecting transaction flow during the selected period.
							</p>
							<div className="element-box">
								{loadingStates.transactionStatusTrends ? (
									<TableLoading />
								) : transactionStatusTrends.length > 0 ? (
									<div style={{ width: '100%', height: 500 }}>
										{/* <ResponsiveContainer>
											<LineChart
												data={transactionStatusTrends}
												margin={{ top: 40, right: 30, left: 20, bottom: 45 }} // More top margin
											>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey="name"
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={timeRanges.transactionStatusTrends === 'pastQuarter' ? 2 : 0}
													tickFormatter={value => {
														const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>
												<Legend wrapperStyle={{ marginBottom: '-45px' }} />
												<Line type="monotone" dataKey="pending" stroke="#FFBB28" strokeWidth={2} name="Pending" connectNulls>
													<LabelList
														dataKey="pending"
														position="top"
														content={renderCustomLabelAlt}
														offset={10}
														style={{ fontSize: '12px', fill: '#333' }}
													/>
												</Line>
												<Line type="monotone" dataKey="completed" stroke="#52c41a" strokeWidth={2} name="Completed" connectNulls>
													<LabelList
														dataKey="completed"
														position="top"
														content={renderCustomLabelAlt}
														offset={10}
														style={{ fontSize: '12px', fill: '#333' }}
													/>
												</Line>
												<Line type="monotone" dataKey="cancelled" stroke="#ff4d4f" strokeWidth={2} name="Cancelled" connectNulls>
													<LabelList
														dataKey="cancelled"
														position="top"
														content={renderCustomLabelAlt}
														offset={10}
														style={{ fontSize: '12px', fill: '#333' }}
													/>
												</Line>
											</LineChart>
										</ResponsiveContainer> */}
										<ResponsiveContainer>
											<AreaChart data={transactionStatusTrends} margin={{ top: 40, right: 30, left: 20, bottom: 45 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey="name"
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={timeRanges.transactionStatusTrends === 'pastQuarter' ? 2 : 0}
													tickFormatter={value => {
														const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												{/* <Tooltip
												formatter={(value) => Number(value).toLocaleString()}
												labelFormatter={(label) =>
													String(label).charAt(0).toUpperCase() + String(label).slice(1)
												}
												/> */}
												<Tooltip
													content={({ active, payload, label }) => {
														if (active && payload && payload.length) {
															const data = payload[0].payload;
															return (
																<div
																	style={{
																		background: '#fff',
																		padding: '10px',
																		border: '1px solid #ccc',
																		borderRadius: '4px',
																	}}
																>
																	<p style={{ fontWeight: 'bold', marginBottom: '8px' }}>{label}</p>
																	<p style={{ color: '#FFBB28', margin: '2px 0' }}>Pending: {data.pending?.toLocaleString()}</p>
																	<p style={{ color: '#52c41a', margin: '2px 0' }}>Completed: {data.completed?.toLocaleString()}</p>
																	<p style={{ color: '#ff4d4f', margin: '2px 0' }}>Cancelled: {data.cancelled?.toLocaleString()}</p>
																	<p style={{ fontWeight: 'bold', marginTop: '8px', color: '#333' }}>
																		Total: {data.total?.toLocaleString()}
																	</p>
																</div>
															);
														}
														return null;
													}}
												/>
												<Legend wrapperStyle={{ marginBottom: '-45px' }} />

												<Area
													type="monotone"
													dataKey="completed"
													stroke="#52c41a"
													fill="#52c41a"
													fillOpacity={0.3}
													strokeWidth={2}
													name="Completed"
													connectNulls
												>
													<LabelList
														dataKey="completed"
														position="top"
														content={renderCustomLabelAlt}
														offset={10}
														style={{ fontSize: '12px', fill: '#333' }}
													/>
												</Area>

												<Area
													type="monotone"
													dataKey="pending"
													stroke="#FFBB28"
													fill="#FFBB28"
													fillOpacity={0.3}
													strokeWidth={2}
													name="Pending"
													connectNulls
												>
													<LabelList
														dataKey="pending"
														position="top"
														content={renderCustomLabelAlt}
														offset={10}
														style={{ fontSize: '12px', fill: '#333' }}
													/>
												</Area>

												<Area
													type="monotone"
													dataKey="cancelled"
													stroke="#ff4d4f"
													fill="#ff4d4f"
													fillOpacity={0.3}
													strokeWidth={2}
													name="Cancelled"
													connectNulls
												>
													<LabelList
														dataKey="cancelled"
														position="top"
														content={renderCustomLabelAlt}
														offset={10}
														style={{ fontSize: '12px', fill: '#333' }}
													/>
												</Area>
											</AreaChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className=" w-100 card d-flex justify-content-center align-items-center" style={{ height: '50px' }}>
										<p className=" mt-6" style={{ paddingTop: '50px' }}>
											No Record for the selected time
										</p>
									</div>
								)}
							</div>
						</div>
					</div>

					<div className="col-12">
						<div className="element-wrapper">
							<h6 className="element-header">Top 10 Payment Method Breakdown</h6>
							<div className="d-flex justify-content-end w-100">
								<div>
									<div
										style={{ width: '100px', cursor: 'pointer', textAlign: 'center' }}
										onClick={() => setModalAlt(true)}
										className="form-control form-control-sm text-white bg-primary rounded"
									>
										View All
									</div>
								</div>
								&nbsp;
								<TimeRangeDropdown value={timeRanges.paymentMethods} onChange={handleTimeRangeChange} chartId="paymentMethods" />
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								This chart presents distribution of top payment methods used by customers such as card payments, bank transfers, or cash.
							</p>
							<div className="element-box">
								{loadingStates.paymentMethods ? (
									<TableLoading />
								) : topPaymentMethods.length > 0 ? (
									<div style={{ width: '100%', height: 400 }}>
										<ResponsiveContainer width="100%" height={400}>
											<BarChart data={topPaymentMethods} margin={{ top: 20, right: 30, left: 20, bottom: 35 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey="name"
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={0}
													tickFormatter={value => {
														const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>
												{/* <Legend wrapperStyle={{marginBottom:'-45px'}}/> */}
												<Bar dataKey="value">
													{topPaymentMethods.map((entry, index) => (
														<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
													))}
													<LabelList dataKey="value" position="top" content={formatBarLabel} />
												</Bar>
											</BarChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className=" w-100 card d-flex justify-content-center align-items-center" style={{ height: '50px' }}>
										<p className=" mt-6" style={{ paddingTop: '50px' }}>
											No Record for the selected time
										</p>
									</div>
								)}
							</div>
						</div>
					</div>

					<div className="col-12">
						<div className="element-wrapper">
							<h6 className="element-header">Bottom 10 Payment Method Breakdown</h6>
							<div className="d-flex justify-content-end w-100">
								<div>
									<div
										style={{ width: '100px', cursor: 'pointer', textAlign: 'center' }}
										onClick={() => setModalAlt(true)}
										className="form-control form-control-sm text-white bg-primary rounded"
									>
										View All
									</div>
								</div>
								&nbsp;
								<TimeRangeDropdown value={timeRanges.paymentMethods} onChange={handleTimeRangeChange} chartId="paymentMethods" />
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								This chart highlights the least utilized payment methods by customers, such as card payments, bank transfers, or cash.
							</p>
							<div className="element-box">
								{loadingStates.paymentMethods ? (
									<TableLoading />
								) : bottomPaymentMethods.length > 0 ? (
									<div style={{ width: '100%', height: 400 }}>
										<ResponsiveContainer width="100%" height={400}>
											<BarChart data={bottomPaymentMethods} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey="name"
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={0}
													tickFormatter={value => {
														const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>
												{/* <Legend /> */}
												<Bar dataKey="value">
													{topPaymentMethods.map((entry, index) => (
														<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
													))}
													<LabelList dataKey="value" position="top" content={formatBarLabel} />
												</Bar>
											</BarChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className=" w-100 card d-flex justify-content-center align-items-center" style={{ height: '50px' }}>
										<p className=" mt-6" style={{ paddingTop: '50px' }}>
											No Record for the selected time
										</p>
									</div>
								)}
							</div>
						</div>
					</div>

					<SlidingPane isOpen={modalAlt}>
						<div className=" content-box">
							<div className=" d-flex justify-content-end w-100">
								<button className="btn btn-danger float-end" onClick={() => setModalAlt(false)}>
									&times;
								</button>
							</div>

							<div className=" d-flex justify-content-end mt-5 t-5 align-items-center">
								<TimeRangeDropdown value={timeRanges.paymentMethods} onChange={handleTimeRangeChange} chartId="paymentMethods" />
							</div>
							<div style={{ paddingTop: '1px', paddingRight: '50px', paddingLeft: '50px', paddingBottom: '1px' }} className="row w-100 ">
								<div className="col-sm-12">
									<div className="element-wrapper">
										<h6 className="element-header">Key Performance Indicators</h6>

										<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
											High-level metrics showing overall metrics of reading over time.
										</p>
										<div className="element-box">
											{/* <TableLoading /> */}

											<Row gutter={16}>
												<Col span={8}>
													<Card>
														<Statistic title="Total Services" value={allPaymentMethods?.length} suffix="" />
														<small className="text-muted">Total services for {timeRanges?.paymentMethods}</small>
													</Card>
												</Col>
												<Col span={8}>
													<Card>
														<Statistic
															title="Total "
															// value={transSummary?.totalRevenueFormatted} suffix=""
														/>

														<small className="text-muted">Cumulative Revenue From --- to --</small>
													</Card>
												</Col>
												<Col span={8}>
													<Card>
														<Statistic
														// title="Total Transactions" value={transSummary?.totalTransactions} suffix=""
														/>
														{/* <Statistic
																					title="Survey Coverage Rate"
																					value={coverageRate > 0 ? (coverageRate).toFixed(1) : 0}
																					suffix=""
																				/> */}
														<small className="text-muted">Cumulative Transactions From --- to --</small>
													</Card>
												</Col>
												{/* <Col span={6}>
																		<Card>
																			<Statistic title="Lowest Scoring Area" value={0} suffix="/3" />
																			<small className="text-muted">Needs attention</small>
																		</Card>
																	</Col> */}
											</Row>
										</div>
									</div>
								</div>
							</div>

							<div className=" " style={{ paddingLeft: '50px', paddingRight: '50px' }}>
								<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666' }}>
									This chart showcases all revenue-generating services, providing a clear view of their performance over time and
									highlighting opportunities for growth or improvement.
								</p>
							</div>

							<div className=" " style={{ paddingLeft: '50px', paddingRight: '50px', paddingBottom: '50px' }}>
								<div className=" card w-100 p-5">
									{loadingStates.paymentMethods ? (
										<TableLoading />
									) : allPaymentMethods.length > 0 ? (
										<div style={{ width: '100%', height: 400 }}>
											<ResponsiveContainer width="100%" height={400}>
												<BarChart data={allPaymentMethods} margin={{ top: 20, right: 30, left: 20, bottom: 35 }}>
													<CartesianGrid strokeDasharray="3 3" />
													<XAxis
														dataKey="name"
														angle={-45}
														textAnchor="end"
														fontSize={12}
														interval={0}
														tickFormatter={value => {
															const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
															const capitalized = truncated
																.toLowerCase()
																.split(' ')
																.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																.join(' ');
															return capitalized;
														}}
													/>
													<YAxis domain={[0, 'auto']} />
													<Tooltip
														formatter={value => Number(value).toLocaleString()}
														labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
													/>
													{/* <Legend wrapperStyle={{marginBottom:'-45px'}}/> */}
													<Bar dataKey="value">
														{topPaymentMethods.map((entry, index) => (
															<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
														))}
														<LabelList dataKey="value" position="top" content={formatBarLabel} />
													</Bar>
												</BarChart>
											</ResponsiveContainer>
										</div>
									) : (
										<div className=" w-100 card d-flex justify-content-center align-items-center" style={{ height: '50px' }}>
											<p className=" mt-6" style={{ paddingTop: '50px' }}>
												No Record for the selected time
											</p>
										</div>
									)}
								</div>
							</div>
						</div>
					</SlidingPane>
				</div>

				{/* Voucher Insights */}
				<div className="row col-12">
					<div className="col-12">
						<div className="element-wrapper">
							<h6 className="element-header">Voucher Usage by Department</h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdown value={timeRanges.voucherUsage} onChange={handleTimeRangeChange} chartId="voucherUsage" />
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								Illustrates the total discount value applied by each department, offering insights into promotional strategies, pricing
								adjustments, and their impact on departmental revenue.
							</p>
							<div className="element-box">
								{loadingStates.voucherUsage ? (
									<TableLoading />
								) : voucherUsage.length > 0 ? (
									<div style={{ width: '100%', height: 400 }}>
										<ResponsiveContainer>
											<BarChart data={voucherUsage} margin={{ top: 20, right: 30, left: 20, bottom: 50 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey="name"
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={timeRanges.voucherUsage === 'pastQuarter' ? 2 : 0}
													tickFormatter={value => {
														const truncated = value.length > 15 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>
												{/* <Legend wrapperStyle={{marginBottom:'-45px'}}/> */}
												<Bar dataKey="value" name="Count">
													{voucherUsage.map((entry, index) => (
														<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
													))}
													<LabelList dataKey="value" position="top" content={formatBarLabel} />
												</Bar>
											</BarChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className=" w-100 card d-flex justify-content-center align-items-center" style={{ height: '50px' }}>
										<p className=" mt-6" style={{ paddingTop: '50px' }}>
											No Record for the selected time
										</p>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>

				{/* Voucher Trends */}
				<div className="row">
					<div className="col-12 ">
						<div className="element-wrapper">
							<h6 className="element-header">Voucher Usage Trends</h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdown value={timeRanges.voucherTrends} onChange={handleTimeRangeChange} chartId="voucherTrends" />
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								Shows how voucher usage changes over time, highlighting patterns in discount redemption and their potential impact on
								revenue.
							</p>
							<div className="element-box">
								{loadingStates.voucherTrends ? (
									<TableLoading />
								) : voucherTrends.length > 0 ? (
									<div style={{ width: '100%', height: 400 }}>
										<ResponsiveContainer>
											<LineChart data={voucherTrends} margin={{ top: 20, right: 30, left: 20, bottom: 45 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey="name"
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={timeRanges.voucherTrends === 'pastQuarter' ? 2 : 0}
													tickFormatter={value => {
														const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>
												<Legend wrapperStyle={{ marginBottom: '-45px' }} />
												<Line type="monotone" dataKey="value" stroke="#8884d8" strokeWidth={2} name="Vouchers" connectNulls>
													<LabelList
														dataKey="vouchers"
														position="top"
														content={renderCustomLabelAlt}
														style={{ fontSize: '12px', fill: '#333' }}
													/>
												</Line>
											</LineChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className=" w-100 card d-flex justify-content-center align-items-center" style={{ height: '50px' }}>
										<p className=" mt-6" style={{ paddingTop: '50px' }}>
											No Record for the selected time
										</p>
									</div>
								)}
							</div>
						</div>
					</div>

					<div className="col-12 col-lg-6">
						<div className="element-wrapper">
							<h6 className="element-header">Transaction Types Count</h6>
							<div className="d-flex justify-content-end w-100">
								<TimeRangeDropdown
									value={timeRanges.transactionTypeCount}
									onChange={handleTimeRangeChange}
									chartId="transactionTypeCount"
								/>
							</div>
							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								This chart compares the total bills generated and Total bills paid transactions, highlighting the balance between incoming
								and outgoing activities.
							</p>
							<div className="element-box">
								{loadingStates.transactionTypeCount ? (
									<TableLoading />
								) : transactionTypeCount.length > 0 ? (
									<div style={{ width: '100%', height: 400 }}>
										<ResponsiveContainer>
											<PieChart width={400} height={400}>
												<Pie
													data={transactionTypeCount}
													dataKey="revenue"
													nameKey="name"
													cx="50%"
													cy="50%"
													outerRadius={150}
													fill="#8884d8"
													label={({ name, value }) => `${Number(value).toLocaleString()}`}
												>
													{transactionTypeCount.map((entry, index) => (
														<Cell key={`cell-${index}`} fill={['green', 'orange'][index % COLORS.length]} />
													))}
												</Pie>
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>
												<Legend formatter={value => value.replace(/\b\w/g, char => char.toUpperCase())} />
											</PieChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className=" w-100 card d-flex justify-content-center align-items-center" style={{ height: '50px' }}>
										<p className=" mt-6" style={{ paddingTop: '50px' }}>
											No Record for the selected time
										</p>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default PaypointStaffAnalytics;
